import { requestUploadUrls } from "../api";
import { supabase } from "../supabase";
import { fingerprintOf } from "./fingerprint";
import { prepareMedia } from "./prepare";

export type UploadPhase = "preparing" | "uploading" | "saving";

/**
 * 送ったのか、既にあったので飛ばしたのか。
 * どちらの場合もメディアIDを返す。飛ばしたものにもタグは付けたい。
 */
export interface UploadOutcome {
  status: "uploaded" | "duplicate";
  mediaId: string;
}

export interface UploadProgress {
  phase: UploadPhase;
  /** 送信済みの割合(0〜1)。phase が uploading のときだけ意味を持つ */
  ratio: number;
}

/**
 * R2 へ送る。
 *
 * fetch ではなく XMLHttpRequest を使うのは、**送信の進捗が取れるのはこちらだけ**のため。
 * 100MB の動画を fetch で送ると、完了まで何の反応も返せず固まったように見える。
 */
function putToStorage(
  url: string,
  body: Blob,
  contentType: string,
  onProgress: (ratio: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const request = new XMLHttpRequest();
    request.open("PUT", url);
    request.setRequestHeader("content-type", contentType);

    request.upload.onprogress = (event) => {
      if (event.lengthComputable) onProgress(event.loaded / event.total);
    };
    request.onload = () => {
      if (request.status >= 200 && request.status < 300) resolve();
      else reject(new Error(`アップロードに失敗しました (${String(request.status)})`));
    };
    // CORS で弾かれると理由は伏せられる。確認先を示さないと原因に辿り着けない
    request.onerror = () => {
      reject(
        new Error(
          "ストレージに接続できませんでした。R2 バケットの CORS 設定を確認してください" +
            "(docs/guides/infrastructure-setup.md 手順3)",
        ),
      );
    };
    request.ontimeout = () => {
      reject(new Error("アップロードがタイムアウトしました"));
    };

    request.send(body);
  });
}

/** 同じ内容のものが既に入っていれば、そのID */
async function existingId(contentHash: string): Promise<string | null> {
  const { data, error } = await supabase
    .from("media")
    .select("id")
    .eq("content_hash", contentHash)
    .is("deleted_at", null)
    .limit(1);
  if (error) throw error;
  const rows: { id: string }[] = data;
  return rows[0]?.id ?? null;
}

/**
 * ファイル1件をアップロードする。
 *
 * 本体は Worker を経由せず R2 へ直接送る。Worker はURLの発行だけを担うため、
 * リクエストサイズやCPU時間の制約を受けない(→ ADR-0005)。
 */
export async function uploadMedia(
  file: File,
  onProgress: (progress: UploadProgress) => void,
): Promise<UploadOutcome> {
  onProgress({ phase: "preparing", ratio: 0 });

  // 変換や送信より先に確かめる。既にあるなら、そこで全部やらずに済む
  const contentHash = await fingerprintOf(file);
  const existing = await existingId(contentHash);
  if (existing !== null) return { status: "duplicate", mediaId: existing };

  const prepared = await prepareMedia(file);
  const target = await requestUploadUrls(prepared.originalMime, prepared.byteSize);

  // 本体とサムネイルを並行して送る。進捗は本体の分だけ見る(サムネイルは数十KB)
  onProgress({ phase: "uploading", ratio: 0 });
  await Promise.all([
    putToStorage(target.originalUrl, prepared.original, prepared.originalMime, (ratio) => {
      onProgress({ phase: "uploading", ratio });
    }),
    putToStorage(target.thumbnailUrl, prepared.thumbnail, "image/webp", () => {
      // サムネイルの進捗は表示しない
    }),
  ]);

  // R2 に置けてからメタデータを入れる。逆順だと、実体の無い行が残り得る
  // couple_id と uploader_id は DB 側の既定値でセッションから埋まる
  // (→ supabase/migrations/20260813000003_media_defaults.sql)
  onProgress({ phase: "saving", ratio: 1 });
  const { error } = await supabase.from("media").insert({
    id: target.mediaId,
    type: prepared.type,
    storage_key: target.storageKey,
    thumbnail_key: target.thumbnailKey,
    mime_type: prepared.originalMime,
    byte_size: prepared.byteSize,
    width: prepared.width,
    height: prepared.height,
    duration_seconds: prepared.durationSeconds,
    taken_at: prepared.takenAt,
    content_hash: contentHash,
  });
  if (error) throw error;
  return { status: "uploaded", mediaId: target.mediaId };
}

/**
 * アップロード中は画面を消灯させない。
 *
 * iOS は画面がロックされるとJavaScriptを止めるため、放置するとアップロードが中断する。
 * バックグラウンド実行(Background Sync / Background Fetch)は Safari が対応しておらず、
 * これが PWA でできる唯一の対処になる(→ ADR-0001)。
 * 取得できない環境でも処理は続行する。
 */
export async function withScreenAwake<T>(run: () => Promise<T>): Promise<T> {
  let sentinel: WakeLockSentinel | null = null;
  try {
    sentinel = await navigator.wakeLock.request("screen");
  } catch {
    // 非対応・拒否のいずれでも、アップロード自体は続ける
  }
  try {
    return await run();
  } finally {
    try {
      await sentinel?.release();
    } catch {
      // 解放できなくても実害はない
    }
  }
}
